import { type Dispatch, type FC, type SetStateAction, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoostInstantCompleteMutation } from '@api/queries/boost';
import { goodsList } from '@common/constants';
import { getCookie } from '@common/lib/cookie';
import { getImage } from '@common/lib/images';
import { type GoodsType } from '@common/types/goods';
import { type PlantTypeText } from '@common/types/plant';
import { CoinBalanceTopUp } from '@components/CoinBalanceTopUp/CoinBalanceTopUp';
import { ResourceItem } from '@components/ResourceItem/ResourceItem';
import {
  H3,
  HugeText,
  StringXSmall,
  TextSmall,
} from '@components/Typography/Typography';
import { PurpleButton } from '@components/ui/Button/Button';
import { CurrencyImage } from '@components/ui/CurrencyImage/CurrencyImage';
import { Picture } from '@components/ui/Picture/Picture';
import { addToStorage } from '@redux/reducers/userReducer';
import { useAppDispatch, useAppSelector } from '@redux/store';
import cn from 'classnames';

import { ModalContext } from '../../Modal/config';

interface BuyInfoProps {
  material: PlantTypeText | GoodsType;
  plantId: string;
  setIsConfirm: Dispatch<SetStateAction<boolean>>;
}

const BuyInfo: FC<BuyInfoProps> = ({ material, setIsConfirm, plantId }) => {
  const { closeModal, boostProductionDynamicData } = useContext(ModalContext);
  const [completeProduction, { isLoading }] = useBoostInstantCompleteMutation();
  const { t } = useTranslation();
  const coinBalance = useAppSelector((state) => state.user.user?.balance.tgem);
  const dispatch = useAppDispatch();

  const confirm = (): void => {
    const withoutConfirm = getCookie<{ status: boolean }>(
      'askForCompleteProductionPurchase',
    );

    if (withoutConfirm?.status) {
      completeProduction({ plantId })
        .unwrap()
        .then(({ plant, condition }) => {
          const payload = {
            item: material,
            value: condition.localStock + condition.incommingStorage,
            costPerUnit: plant.costPerUnit,
          };
          dispatch(addToStorage(payload));
        });
      closeModal();
    } else {
      setIsConfirm(true);
    }
  };

  const icon = getImage({ name: material, field: 'icon' });

  return (
    <>
      {goodsList.includes(material) ? (
        <div
          className="rounded-lg bg-white/10 mb-2 shrink-0 relative"
          style={{ width: '5.5rem', height: '5.5rem' }}
        >
          <Picture
            className="w-full h-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            img={icon?.img}
            webp={icon?.webp}
            alt={material}
          />
        </div>
      ) : (
        <ResourceItem
          radius="default"
          className="mb-2 shrink-0"
          kind={material as PlantTypeText}
          variant="light"
          size={{ width: '5.5rem', height: '5.5rem' }}
        />
      )}
      <HugeText className="mb-1">{boostProductionDynamicData?.inPool}</HugeText>
      <StringXSmall weight="regular" className="!text-white/40 mb-4">
        {t('in pool')}
      </StringXSmall>
      <TextSmall
        weight="regular"
        className="!leading-1.2 mb-6 max-w-[12.875rem] text-center"
      >
        {t('Instantly complete the production line')}
      </TextSmall>
      <div className="w-[12.75rem] relative mb-10">
        <PurpleButton
          onClick={confirm}
          isLoading={isLoading}
          disabled={
            (coinBalance || 0) < (boostProductionDynamicData?.price || 0) ||
            isLoading
          }
          inactive={
            (coinBalance || 0) < (boostProductionDynamicData?.price || 0)
          }
          className={cn(
            'flex items-center gap-1',
            (coinBalance || 0) < (boostProductionDynamicData?.price || 0) &&
              '!bg-transparent',
          )}
        >
          <CurrencyImage kind="tgem" size="1.5rem" />
          <H3
            weight="extrabold"
            className={
              (coinBalance || 0) < (boostProductionDynamicData?.price || 0)
                ? 'text-white/40'
                : 'white-text-shadow-gradient'
            }
          >
            {boostProductionDynamicData?.price || 0}
          </H3>
        </PurpleButton>
        {(coinBalance || 0) < (boostProductionDynamicData?.price || 0) && (
          <StringXSmall
            className="w-full absolute left-1/2 -translate-x-1/2 top-[calc(100%+0.5rem)] text-center text-white/40"
            weight="regular"
          >
            {t('Not enough T-Gem')}
          </StringXSmall>
        )}
      </div>
      <CoinBalanceTopUp
        className="!h-12 !pl-2 !pr-3 py-[0.625rem]"
        contenctClassName="!items-start"
        compare={boostProductionDynamicData?.price || 0}
        iconSize="1.75rem"
      />
    </>
  );
};

export { BuyInfo };
